import { Button, Select, SelectOptionProps, Text } from '@televet/kibble-ui';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import React from 'react';
import { usePagination } from './usePagination';

export type StepPaginationProps = {
  recordCount: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  options?: number[];
  isLoading?: boolean;
  dataTestId?: string;
};

const defaultOptions = [5, 10, 15, 20];

export function StepPagination({
  recordCount,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  options = defaultOptions,
  dataTestId,
}: StepPaginationProps): JSX.Element {
  const { totalPageCount } = usePagination({
    recordCount,
    pageSize,
    currentPage,
  });

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, recordCount);

  const memoizeOptions = React.useMemo<SelectOptionProps[]>(
    () =>
      options.map(
        (option) =>
          ({
            label: (
              <Text
                size="sm"
                variant={pageSize === option ? 'interactive' : undefined}
                data-testid={`step-pagination-option${dataTestId ? `-${dataTestId}` : ''}`}
              >
                {option} per page
              </Text>
            ),
            value: option.toString(),
          } as SelectOptionProps),
      ),
    [dataTestId, options, pageSize],
  );

  return (
    <Flex gap={2} position="relative">
      <Box>
        <Select
          containerProps={{
            strategy: 'fixed',
            placement: 'top',
            closeOnSelect: true,
            closeOnBlur: true,
            isLazy: true,
          }}
          variant="outline"
          width="auto"
          minH="auto"
          value={pageSize.toString()}
          borderRadius={8}
          borderColor="border.default"
          options={memoizeOptions}
          renderCustomSelectedOption={(option): JSX.Element => (
            <Text
              fontSize="sm"
              fontWeight="bold"
              as="p"
              py={1}
              data-testid={`step-pagination-page-size${dataTestId ? `-${dataTestId}` : ''}`}
            >
              {option?.value}{' '}
              <Text variant="subtle" fontWeight="normal">
                per page
              </Text>
            </Text>
          )}
          listProps={{
            onSelect: (e): void => {
              onPageSizeChange(parseFloat(e?.value || '1'));
            },
            shouldRetainSelection: false,
          }}
        />
      </Box>

      <Flex borderWidth="1px" borderRadius={8} borderColor="border.default" alignItems="stretch">
        <Flex borderRightWidth="1px" borderColor="border.default" alignItems="center">
          <Text
            fontWeight="black"
            fontSize="sm"
            px={3}
            data-testid={`step-pagination-count${dataTestId ? `-${dataTestId}` : ''}`}
          >
            {`${startItem}-${endItem} `}
            <Text variant="subtle" fontWeight="normal" fontSize="sm">
              of {recordCount}
            </Text>
          </Text>
        </Flex>

        <Flex gap={1}>
          <Button
            data-testid={`step-pagination-previous${dataTestId ? `-${dataTestId}` : ''}`}
            size="sm"
            iconName="chevronLeft"
            aria-label="Previous Page"
            isDisabled={currentPage === 1}
            variant="ghost"
            onClick={(): void => onPageChange(currentPage - 1)}
          />
          <Button
            data-testid={`step-pagination-next${dataTestId ? `-${dataTestId}` : ''}`}
            size="sm"
            iconName="chevronRight"
            aria-label="Next Page"
            isDisabled={currentPage === totalPageCount}
            variant="ghost"
            onClick={(): void => onPageChange(currentPage + 1)}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}
